import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import NAV from './adminNav.json';

export const SearchIcon = ({ size = 16, fill = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill={fill}><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" /></svg>
);

export const openAdminSearch = () => {
  if (window.openGlobalSearchModal) window.openGlobalSearchModal();
};

export const openAdminJump = () => {
  if (window.openJumpModal) window.openJumpModal();
};

const GroupIcon = ({ path }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d={path} /></svg>
);

const Chevron = ({ size = 12, opacity = 0.6 }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity }}><path d="M2.5 4.5 6 8l3.5-3.5" /></svg>
);

const COLUMN_THRESHOLD = 12;

const NavLink = ({ link }) => (
  <a href={link.path} role="menuitem">{link.label}</a>
);

const NavSubgroup = ({ link }) => (
  <div className="adash-nav-subgroup">
    <span className="adash-nav-subgroup-label">{link.label}</span>
    {link.children.map((child) => <NavLink link={child} key={child.path} />)}
  </div>
);

const initials = (name = '') => name.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('') || 'A';

const AdminChrome = ({ environment, logoUrl, user = {}, sidekiqUrl, signOutUrl, children }) => {
  const [openGroup, setOpenGroup] = useState(null);
  const [userOpen, setUserOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (rootRef.current && !e.target.closest('.adash-navgroup')) setOpenGroup(null);
      if (!e.target.closest('.adash-user')) setUserOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') { setOpenGroup(null); setUserOpen(false); }
    };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const signOut = () => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = signOutUrl || '/users/sign_out';
    const method = document.createElement('input');
    method.type = 'hidden'; method.name = '_method'; method.value = 'delete';
    form.appendChild(method);
    const token = document.querySelector('meta[name="csrf-token"]');
    if (token) {
      const csrf = document.createElement('input');
      csrf.type = 'hidden'; csrf.name = 'authenticity_token'; csrf.value = token.content;
      form.appendChild(csrf);
    }
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="adash" ref={rootRef}>
      <div className="adash-topbar">
        <div className="adash-topbar-inner">
          <div className="adash-brand">
            {logoUrl ? <a href="/admin"><img src={logoUrl} alt="Opendate" /></a> : null}
            <span className="adash-brand-sep" />
            <span className="adash-brand-label">Admin</span>
            {environment ? <span className="adash-env"><span className="adash-env-dot" />{environment}</span> : null}
          </div>

          <div className="adash-topbar-actions">
            <button type="button" className="adash-chip-btn" onClick={openAdminSearch}>
              <SearchIcon />Search<span className="adash-kbd">&#8984;K</span>
            </button>
            <button type="button" className="adash-chip-btn" onClick={openAdminJump}>
              <SearchIcon />Jump<span className="adash-kbd">&#8984;J</span>
            </button>

            <div className="adash-user">
              <button type="button" className="adash-user-btn" aria-haspopup="menu" aria-expanded={userOpen} onClick={(e) => { e.stopPropagation(); setUserOpen((o) => !o); }}>
                <span className="adash-avatar">{initials(user.name)}</span>
                <span className="adash-user-name">{user.name}</span>
                <Chevron opacity={0.8} />
              </button>
              {userOpen ? (
                <div className="adash-user-menu">
                  <div className="adash-user-menu-head">{user.email || user.name}</div>
                  {sidekiqUrl ? <a href={sidekiqUrl} target="_blank" rel="noreferrer">Sidekiq</a> : null}
                  <button type="button" onClick={signOut}>Logout</button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <div className="adash-navbar">
        <div className="adash-navbar-inner">
          {NAV.map((group) => (
            <div className={`adash-navgroup${openGroup === group.title ? ' open' : ''}`} key={group.title}>
              <button type="button" className="adash-nav-trigger" aria-haspopup="menu" aria-expanded={openGroup === group.title} onClick={(e) => { e.stopPropagation(); setOpenGroup((g) => (g === group.title ? null : group.title)); }}>
                <span className="adash-nav-icon"><GroupIcon path={group.icon} /></span>
                {group.title}
                <Chevron />
              </button>
              {openGroup === group.title ? (
                <div className={`adash-nav-dropdown${group.links.length > COLUMN_THRESHOLD ? ' adash-nav-dropdown--columns' : ''}`} role="menu">
                  {group.links.map((link) => (link.children
                    ? <NavSubgroup link={link} key={link.label} />
                    : <NavLink link={link} key={link.path} />))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {children}

      <footer className="adash-sitefooter">
        &#169; {new Date().getFullYear()} Opendate, Inc. All Rights Reserved.
      </footer>
    </div>
  );
};

AdminChrome.propTypes = {
  environment: PropTypes.string,
  logoUrl: PropTypes.string,
  user: PropTypes.object,
  sidekiqUrl: PropTypes.string,
  signOutUrl: PropTypes.string,
  children: PropTypes.node,
};

export default AdminChrome;
